import { useState } from "react";
import { ArrowRight, Building2, Layers3, MapPin, Phone, ShieldCheck } from "lucide-react";
import Header from "@/components/landing/Header";
import Footer from "@/components/landing/Footer";
import { Button } from "@/components/ui/button";
import CalendlyModal from "@/components/CalendlyModal";
import SEOHead from "@/components/SEOHead";

const APropos = () => {
  const [calendlyOpen, setCalendlyOpen] = useState(false);

  const pillars = [
    {
      icon: Phone,
      title: "Les appels entrants",
      description:
        "Immovox prend le relais lorsque l'agence ne peut pas décrocher, identifie le motif de l'appel et recueille les informations prévues par le scénario.",
    },
    {
      icon: Building2,
      title: "Les workflows immobiliers",
      description:
        "Les parcours sont pensés autour des demandes réelles d'une agence : estimation, vente, acquisition, location, gestion et demandes administratives.",
    },
    {
      icon: ShieldCheck,
      title: "Des règles explicites",
      description:
        "L'assistant suit les consignes configurées par l'agence. Lorsqu'une information manque, l'objectif est de transmettre le contexte plutôt que d'inventer une réponse.",
    },
    {
      icon: Layers3,
      title: "Une base produit partagée",
      description:
        "Immovox est la déclinaison spécialisée immobilier de la technologie développée par KALYVOX SASU. Kalyvox couvre les autres secteurs d'activité.",
    },
  ];

  return (
    <div className="min-h-screen bg-background">
      <SEOHead
        title="À propos d'Immovox | IA téléphonique pour l'immobilier"
        description="Immovox est la marque de KALYVOX SASU dédiée aux appels des agences immobilières : accueil, qualification par scénario et synthèse exploitable."
        canonical="/a-propos"
      />
      <Header />

      <main>
        <section className="pt-32 pb-20 px-4">
          <div className="max-w-4xl mx-auto text-center">
            <div className="eyebrow mb-6">
              <MapPin className="w-4 h-4" />
              KALYVOX SASU · Toulouse, France
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-semibold tracking-[-.045em] text-foreground mb-6 leading-[1.03]">
              Immovox, l'IA téléphonique
              <br />
              <span className="text-primary">spécialisée pour l'immobilier</span>
            </h1>
            <p className="text-lg md:text-xl text-muted-foreground max-w-3xl mx-auto leading-relaxed">
              Immovox est une marque de KALYVOX SASU dédiée aux agences immobilières.
              Le service aide les équipes à traiter les appels qu'elles ne peuvent pas prendre
              immédiatement, sans transformer chaque conversation en script rigide.
            </p>
          </div>
        </section>

        <section className="py-20 px-4 bg-secondary/50">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-semibold text-foreground mb-8">
              Pourquoi une déclinaison dédiée à l'immobilier ?
            </h2>
            <div className="space-y-6 text-lg text-muted-foreground leading-relaxed">
              <p>
                Les appels d'une agence ne se ressemblent pas. Un propriétaire qui demande une estimation,
                un acquéreur intéressé par une annonce, un candidat locataire et un client en gestion
                n'attendent ni les mêmes questions ni la même suite.
              </p>
              <p>
                Immovox concentre donc l'expérience produit, les scénarios et le contenu métier sur ces
                situations. L'objectif n'est pas de répondre à tout à la place de l'agence, mais de
                comprendre la demande, recueillir les informations utiles et préparer l'action suivante.
              </p>
              <p>
                Cette spécialisation permet également de documenter précisément les sujets qui entourent
                le téléphone en agence : standard, permanence, accueil, appels manqués, qualification et
                organisation de la téléphonie.
              </p>
            </div>
          </div>
        </section>

        <section className="py-20 px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-14">
              <h2 className="text-3xl md:text-4xl font-semibold text-foreground mb-4">
                Ce que couvre Immovox
              </h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                Un périmètre volontairement clair : répondre, qualifier, transmettre et appliquer les règles définies par l'agence.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              {pillars.map((pillar) => (
                <div key={pillar.title} className="border-t border-foreground/20 pt-6">
                  <div className="w-10 h-10 border border-border flex items-center justify-center mb-5">
                    <pillar.icon className="w-6 h-6 text-primary" />
                  </div>
                  <h3 className="text-xl font-semibold text-foreground mb-2">{pillar.title}</h3>
                  <p className="text-muted-foreground leading-relaxed">{pillar.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-20 px-4 bg-secondary/50">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-semibold text-foreground mb-8">
              Immovox et Kalyvox
            </h2>
            <div className="space-y-5 text-lg text-muted-foreground leading-relaxed">
              <p>
                Immovox et Kalyvox sont édités par la même société, KALYVOX SASU.
                Kalyvox porte le produit multi-sectoriel ; Immovox conserve une vitrine, un positionnement
                et des contenus exclusivement orientés immobilier.
              </p>
              <p>
                Cette organisation permet de faire évoluer la technologie une seule fois tout en gardant
                une expérience et une documentation adaptées aux agences immobilières.
              </p>
              <p>
                Les informations juridiques, l'éditeur du site et les informations d'hébergement sont
                disponibles dans nos <a href="/mentions-legales" className="text-primary hover:underline">mentions légales</a>.
                Pour les autres secteurs, consultez{" "}
                <a href="https://kalyvox.ai" target="_blank" rel="noopener" className="text-primary hover:underline">
                  kalyvox.ai
                </a>.
              </p>
            </div>
          </div>
        </section>

        <section className="py-16 px-4 bg-[hsl(157_24%_15%)]">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-semibold text-background mb-5">
              Voir Immovox sur un scénario réel d'agence
            </h2>
            <p className="text-lg text-background/80 mb-8">
              Testez les parcours vendeur, acquéreur, location ou gestion et vérifiez le niveau de contexte transmis après l'appel.
            </p>
            <Button
              size="lg"
              variant="outline"
              onClick={() => setCalendlyOpen(true)}
              className="rounded-md border-background/30 bg-background text-foreground hover:bg-background/90 px-7"
            >
              Planifier une démo
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </div>
        </section>
      </main>

      <Footer />
      <CalendlyModal open={calendlyOpen} onOpenChange={setCalendlyOpen} />
    </div>
  );
};

export default APropos;
